import React, { useState } from 'react';
import { Badge } from '../../CommonComponents/OPL/Badge/Badge';
import { Box } from '../../CommonComponents/OPL/Box/Box';
import { Button } from '../../CommonComponents/OPL/Button/Button';
import { Card } from '../../CommonComponents/OPL/Card/Card';
import { Icon } from '../../CommonComponents/OPL/Icon/Icon';
import { Modal } from '../../CommonComponents/OPL/Modal/Modal';
import { ModalBody } from '../../CommonComponents/OPL/Modal/ModalBody';
import { ModalCloseButton } from '../../CommonComponents/OPL/Modal/ModalCloseButton';
import { ModalFooter } from '../../CommonComponents/OPL/Modal/ModalFooter';
import { ModalHeader } from '../../CommonComponents/OPL/Modal/ModalHeader';
import { Stat } from '../../CommonComponents/OPL/Stat/Stat';
import { Text } from '../../CommonComponents/OPL/Text/Text';
import { BatchItemResult } from '../api/pdfBatchTypes';
import {
  StyledMarkdown,
  PeopleCardsRow,
  StatsRow,
  InsightCard,
  WarningCard,
  SectionHeader,
  WarningsToggle,
} from './ItemInfoModal.style';
import {
  Heading1,
  Heading2,
} from '../../ApexOPL/AccountOpening/fields/ESigned/components/Disclosures/Disclosures.styles';

interface Props {
  item: BatchItemResult;
  onClose(): void;
}

const formatCurrency = (value: number): string => {
  const sign = value < 0 ? '-' : '';
  const abs = Math.abs(value);
  if (abs >= 999_950_000) return `${sign}$${(abs / 1_000_000_000).toFixed(1)}B`;
  if (abs >= 1_000_000) return `${sign}$${(abs / 1_000_000).toFixed(1)}M`;
  if (abs >= 999_500) return `${sign}$1.0M`;
  if (abs >= 1_000) return `${sign}$${(abs / 1_000).toFixed(0)}K`;
  return `${sign}$${abs.toLocaleString()}`;
};

const formatDate = (isoString: string | null): string => {
  if (!isoString) return '';
  const date = new Date(isoString);
  if (isNaN(date.getTime())) return '';
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};

export const ItemInfoModal = ({ item, onClose }: Props) => {
  const issueCount = item.warnings.length + item.errors.length;
  const [warningsOpen, setWarningsOpen] = useState(false);

  const importDate = formatDate(item.processedAtUtc);
  const hasFinancialSummary = item.netWorth != null || item.annualIncome != null;

  return (
    <Modal
      isOpen
      onRequestClose={onClose}
      size="large"
      aria={{ labelledby: 'item-info-modal-heading' }}
    >
      <ModalHeader id="item-info-modal-heading">
        <Box css={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flex: 1 }}>
          <Box>
            <Text fontSize="h4" fontWeight={600}>
              {item.fileName}
            </Text>
            {importDate && (
              <Text fontSize="small" color="neutral.500" mt="4">
                Imported {importDate}
              </Text>
            )}
          </Box>
        </Box>
      </ModalHeader>
      <ModalCloseButton onClick={onClose} />

      <ModalBody>
        {item.summary && (
          <Box mb="16">
            <StyledMarkdown
              options={{
                overrides: {
                  h1: { component: Heading1 },
                  h2: { component: Heading2 },
                },
              }}
            >
              {item.summary}
            </StyledMarkdown>
          </Box>
        )}

        <PeopleCardsRow>
          {item.principalName && (
            <Card p="16" hasBorder>
              <Text variant="smallCaps" fontSize="h6" color="neutral.500" mb="4">
                Principal
              </Text>
              <Text fontWeight={600} fontSize="h5">
                {item.principalName}
              </Text>
              {item.principalAge != null && (
                <Text fontSize="small" color="neutral.500" mt="2">
                  Age {item.principalAge}
                </Text>
              )}
            </Card>
          )}

          {item.spouseName && (
            <Card p="16" hasBorder>
              <Text variant="smallCaps" fontSize="h6" color="neutral.500" mb="4">
                Spouse
              </Text>
              <Text fontWeight={600} fontSize="h5">
                {item.spouseName}
              </Text>
              {item.spouseAge != null && (
                <Text fontSize="small" color="neutral.500" mt="2">
                  Age {item.spouseAge}
                </Text>
              )}
            </Card>
          )}

          {hasFinancialSummary && (
            <Card p="16" hasBorder>
              <Text variant="smallCaps" fontSize="h6" color="neutral.500" mb="4">
                Net Worth
              </Text>
              <Text fontWeight={600} fontSize="h5">
                {item.netWorth != null ? formatCurrency(item.netWorth) : '—'}
              </Text>
              {item.annualIncome != null && (
                <Text fontSize="small" color="neutral.500" mt="2">
                  Income {formatCurrency(item.annualIncome)}/yr
                </Text>
              )}
            </Card>
          )}
        </PeopleCardsRow>

        <Card p="16" mb="16" hasBorder>
          <StatsRow>
            <Stat size="small" label="Accounts" value={item.accountCount.toString()} />
            <Stat size="small" label="Goals" value={item.goalCount.toString()} />
            <Stat size="small" label="Incomes" value={item.incomeCount.toString()} />
            <Stat size="small" label="Policies" value={item.insuranceCount.toString()} />
            <Stat size="small" label="Expenses" value={item.expenseCount.toString()} />
          </StatsRow>
        </Card>

        {item.planningInsights && item.planningInsights.length > 0 && (
          <Box mb="16">
            <SectionHeader>
              <Text fontSize="h5" fontWeight={600}>
                Potential Next Steps
              </Text>
              <Text fontSize="small" color="neutral.400">
                Planning insights
              </Text>
            </SectionHeader>
            {item.planningInsights.map((insight, i) => (
              <InsightCard key={i} category={insight.category}>
                <Text fontWeight={600} fontSize="body" mb="4">
                  {insight.title}
                </Text>
                <Text fontSize="small" color="neutral.600">
                  {insight.description}
                </Text>
              </InsightCard>
            ))}
          </Box>
        )}

        {issueCount > 0 && (
          <Box>
            <WarningsToggle
              onClick={() => setWarningsOpen(prev => !prev)}
              role="button"
              tabIndex={0}
              onKeyDown={e => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  setWarningsOpen(prev => !prev);
                }
              }}
            >
              <Icon
                className={`fas fa-chevron-${warningsOpen ? 'down' : 'right'}`}
                fontSize="small"
                color="neutral.500"
              />
              <Text fontSize="h5" fontWeight={600}>
                Needs Your Input
              </Text>
              <Badge variant="warning" size="small">
                {issueCount}
              </Badge>
            </WarningsToggle>

            {warningsOpen && (
              <Box mt="8">
                {item.errors.map((error, i) => (
                  <WarningCard key={`e-${i}`}>
                    <Text color="danger.500" fontSize="small" css={{ flexShrink: 0 }}>
                      <Icon className="fas fa-times-circle" />
                    </Text>
                    <Text fontSize="small" color="danger.600">
                      {error}
                    </Text>
                  </WarningCard>
                ))}

                {item.warnings.map((warning, i) => (
                  <WarningCard key={`w-${i}`}>
                    <Text color="warning.500" fontSize="small" css={{ flexShrink: 0 }}>
                      <Icon className="fas fa-exclamation-triangle" />
                    </Text>
                    <Text fontSize="small">{warning}</Text>
                  </WarningCard>
                ))}
              </Box>
            )}
          </Box>
        )}
      </ModalBody>

      <ModalFooter>
        <Button variant="subtleInactive" onClick={onClose}>
          Close
        </Button>
        {item.householdId && (
          <Button
            onClick={() => ADV.util.Router.go2('ConsumerOverview', { clientId: item.householdId })}
            ml="12"
          >
            View Household
          </Button>
        )}
      </ModalFooter>
    </Modal>
  );
};
